import { Firm } from '../firm/model';
import { FundSponsor } from '../fund-sponsor/model';
import { Nullable } from '@trade-platform/ui-utils';
import { BaseUser } from '../user/model';
import { ExternalFirm } from '../fund-firms/model';
/** Keys the UI knows how to display. The section is hidden unless at least one is present. */
export const KNOWN_EMAIL_PREFERENCE_KEYS = [
    'dailyOrderDigest',
    'newProductAvailable',
    'newOrderStarted'
] as const;

export type EmailPreferenceKey = (typeof KNOWN_EMAIL_PREFERENCE_KEYS)[number];

export type EmailPreferences = Partial<Record<EmailPreferenceKey, boolean>>;

export interface ProfileHoldingOption {
    id: string;
    name: string;
    shortCode: string;
    zip?: string;
    city?: string;
    phone?: string;
    state?: string;
    addressLine1?: string;
    addressLine2?: string;
    accountPrefix?: string;
    accountPostfix?: string;
    externalCustodianId?: string;
    custodianTaxId?: string;
}

export type UserTenantDataTypes = 'firm' | 'holdingOption' | 'fundSponsor' | 'transferAgent';

export interface ProfileSubTenant {
    entityId: number;
    type: UserTenantDataTypes;
}

export interface ProfileOrganization {
    id?: number;
    tenantIdentifier: string;
    entityId: number;
    type: UserTenantDataTypes;
    extension?: string;
    subTenants?: ProfileSubTenant[];
    supportPhoneNumber?: string;
    supportEmail?: string;
    languageOptions?: Record<string, any>;
    routing?: Record<string, unknown>;
}

export interface UserEventHistory {
    createdAt: string;
    updatedBy: number;
    passwordResetDate?: string;
    accountSetupDate?: string;
    editDate?: string;
    resetMFADate?: string;
    isPasswordResetExpired?: boolean;
    isAccountSetupExpired?: boolean;
    passwordResetExpirationDate?: string;
    accountSetupExpirationDate?: string;
}

export interface Profile extends BaseUser {
    id?: string;
    crdNumber?: string;
    email: string;
    features: Feature[];
    firm?: Firm;
    holdingOption?: ProfileHoldingOption;
    fundSponsor?: FundSponsor;
    firstName: string;
    fullName: string;
    lastName: string;
    repCodes?: ProfileRepCode[];
    roles: Role[];
    acceptedTerms: boolean;
    identityProviderId: number;
    officeName?: string;
    officeId?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    phoneMobile?: string;
    organization?: ProfileOrganization | null;
    firms?: ExternalFirm[];
    eventHistory?: UserEventHistory;
    emailPreferences?: EmailPreferences;
}

export type UpdateProfilePayload = Nullable<
    Pick<
        Profile,
        | 'firstName'
        | 'lastName'
        | 'crdNumber'
        | 'officeName'
        | 'addressStreet'
        | 'addressCity'
        | 'addressState'
        | 'addressZip'
        | 'phoneMobile'
        | 'firmAddress'
        | 'branchAddress'
        | 'emailPreferences'
    >
>;

export interface Feature {
    name: string;
}

/**
 * Pylon support-chat configuration for the logged-in user, served by advisors-api.
 *
 * `emailHash` is an HMAC of `email` computed server-side with the Pylon identity-verification
 * secret. Pylon works out who the chat user is from the email the browser sets, so this hash is
 * what stops one user identifying as another and reading their support conversations. The widget
 * must identify with exactly the `email` returned here, or the hash will not match it.
 */
export interface PylonIdentity {
    appId: string;
    email: string;
    name: string;
    emailHash: string;
    /** Tenant routing key for Pylon's trigger rules. Absent for users with no organization. */
    tenantIdentifier?: string;
}

export interface Role {
    name: string;
}

export interface ProfileRepCode {
    id: string;
    fullName?: string;
    repCode?: string;
    listName?: string;
    custodianName?: string;
    repCodeSubstring?: string;
    firmCodeSubstring?: string;
    branchCodeSubstring?: string;
    users?: Array<RepCodeUsers>;
}

export interface RepCodeUsers {
    id?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string;
    birthDate?: string;
    banned?: boolean;
    firmAdmin?: boolean;
    tenantCode?: string;
}

export interface SharedExternalFirm {
    externalFirmId?: string;
    name: string;
}
